import React, { useEffect, useMemo, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import Close from "../../../../../images/Close.svg";
import { Button } from "../../../../../stories/Button/Button";
import { Label } from "../../../../../stories/Label/Label";
import Input from "../../../../../stories/Input/Input";
import useServerDateandTime from "../../../../../customHooks/useServerDateandTime";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GetProjectDailyReportsById,
  GetProjectsHeaders,
} from "../../masters/projects/queries/queries";
import {
  AddProjectDailyReportsResult,
  AddProjectDailyReportsVar,
  AtsProjectDailyReports,
  GetProjectsHeadersResult,
  GetProjectsHeadersVar,
  UpdateProjectDailyReportsResult,
  UpdateProjectDailyReportsVar,
} from "../../masters/projects/queries/types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import {
  handleFormEvent,
  toInputStandardDate,
  toIsoDate,
} from "../../../../../utils/UtilFunctions";
import { DataTypes, Operation } from "../../../../../utils/Enum.types";
import InputNumber from "../../../../common/Input/InputNumber";
import { FormControlLabel } from "@mui/material";
import { AntSwitch } from "../../../../../styles/Switch";
import { nodeVar } from "../../../../../utils/commonQueryTypes";
import {
  AddProjectDailyReports,
  UpdateProjectDailyReports,
} from "../../masters/projects/queries/mutations";
import { emptyMessage } from "../../../../../utils/constants";
import { msgType } from "../../../../../utils/types";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import MessageModal from "../../../../../customComponents/MessageModal";
import LoadingModal from "../../../../pages/LoadingModal";

interface Props {
  setDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  operation: Operation;
  reportsID: number;
}

interface FormFields {
  labelName: string;
  inputName: string;
  dataType: string;
  autoFocus: boolean;
  required: boolean;
  disabled: boolean;
}

const FillProgressDetails = ({ setDrawer, operation, reportsID }: Props) => {
  const { customer_id, projectID } = useUrlParams();

  const { serverDate } = useServerDateandTime();

  const [formFields, setFormFields] = useState<FormFields[]>([]);
  const [formData, setFormData] = useState<
    Record<string, string | number | boolean>
  >({});

  const { user_details } = useLoggedInUserDetails();
  const [message, setMessage] = useState<msgType>(emptyMessage);

  const [AddInitData, { loading }] = useMutation<
    AddProjectDailyReportsResult,
    AddProjectDailyReportsVar
  >(AddProjectDailyReports, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [updateProjectDailyReports, { loading: updateLoading }] = useMutation<
    UpdateProjectDailyReportsResult,
    UpdateProjectDailyReportsVar
  >(UpdateProjectDailyReports, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [getProjectDailyReportsById, { data: roprtsLoading }] = useLazyQuery<
    { node: AtsProjectDailyReports },
    nodeVar
  >(GetProjectDailyReportsById);

  const [GetHeaders, { data: headerData }] = useLazyQuery<
    GetProjectsHeadersResult,
    GetProjectsHeadersVar
  >(GetProjectsHeaders);

  useEffect(() => {
    if (customer_id && projectID) {
      GetHeaders({
        variables: {
          customerID: customer_id,
          projectID: projectID,
        },
      }).then(({ data }) => {
        if (data && data.GetProjectsHeaders) {
   
          
          setFormFields(
            data.GetProjectsHeaders.filter((head)=> reportsID === 0  ? head.initData === false : true).map((header, index) => ({
              labelName: header.header,
              inputName: `${header.idx}`,
              dataType: header.dataType,
              autoFocus: false,
              required: false,
              disabled: header.initData,
            }))
          );

          setFormData(
            data.GetProjectsHeaders.reduce((acc, curr) => {
              if (curr.dataType === DataTypes.BOOLEAN) {
                acc[curr.idx] = false;
              } else acc[curr.idx] = "";
              return acc;
            }, {} as Record<string, string | number | boolean>)
          );
        }
      });
    }
  }, [serverDate, GetHeaders, customer_id, projectID, headerData]);

  useEffect(() => {
    if (customer_id && projectID) {
      getProjectDailyReportsById({
        variables: {
          id: reportsID ? Number(reportsID) : 0,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          const node = data.node;

          setFormData(
            formFields.reduce((acc, curr) => {
              if (curr.dataType === DataTypes.DATE) {
                acc[curr.inputName] = (node as any)[`param${curr.inputName}`]
                  ? toInputStandardDate((node as any)[`param${curr.inputName}`])
                  : "";
              } else if (curr.dataType === DataTypes.BOOLEAN) {
                let value: boolean =
                  (node as any)[`param${curr.inputName}`].toLowerCase() ===
                  true;

                acc[curr.inputName] = value;
              } else {
                acc[curr.inputName] = (node as any)[`param${curr.inputName}`];
              }
              return acc;
            }, {} as Record<string, string | number | boolean>)
          );
        }
      });
    }
  }, [
    customer_id,
    projectID,
    getProjectDailyReportsById,
    reportsID,
    formFields,
    operation,
  ]);

  const modifiedFormFields = useMemo(() => {
    return [
      {
        labelName: "Today",
        inputName: "today",
        dataType: "date",
        autoFocus: false,
        required: false,
        disabled: true,
      },
      ...formFields,
    ];
  }, [headerData, formFields]);

  const handleSubmit = () => {
    if (customer_id && projectID) {
      if (operation === Operation.CREATE) {
        AddInitData({
          variables: {
            customerID: customer_id,
            projectID: projectID,
            reportDate: toIsoDate(serverDate),
            input: Object.entries(formData).map(([key, value]) => {
              const foundData = modifiedFormFields
                ? modifiedFormFields.find(
                    (header) => Number(header.inputName) === Number(key)
                  )
                : undefined;
              if (foundData && foundData.dataType === DataTypes.DATE) {
                return {
                  idx: Number(key),
                  param: toIsoDate(value as string),
                };
              }
              return {
                idx: Number(key),
                param: value.toString(),
              };
            }),
            user_details,
          },
        }).then(({ data }) => {
          if (data && data.AddProjectDailyReports) {
            setMessage({
              flag: true,
              message: `Initialize Data added successfully`,
              operation: Operation.CREATE,
            });
          }
        });
      } else if (operation === Operation.UPDATE) {
        updateProjectDailyReports({
          variables: {
            customerID: customer_id,
            projectID: projectID,
            AtsProjectDailyReportsID: reportsID,
            input: Object.entries(formData).map(([key, value]) => {
              const foundData = headerData
                ? headerData.GetProjectsHeaders.find(
                    (header) => header.idx === Number(key)
                  )
                : undefined;
              if (foundData && foundData.dataType === DataTypes.DATE) {
                return {
                  idx: Number(key),
                  param: toIsoDate(value as string),
                };
              }
              return {
                idx: Number(key),
                param: value.toString(),
              };
            }),
            user_details,
          },
        }).then(({ data }) => {
          if (data && data.UpdateProjectDailyReports) {
            setMessage({
              flag: true,
              message: `Initialize Data updated successfully`,
              operation: Operation.UPDATE,
            });
          }
        });
      }
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <form className="fill-progress-details">
        <div className="fill-progress-details__title">
          <Title>Fill Work Progress</Title>
          <img src={Close} alt="" onClick={() => setDrawer(false)} />
        </div>
        <div className="fill-progress-details__block">
          {modifiedFormFields.map((label, index) => {
            if (label.inputName === "today") {
              return (
                <div key={index} className="fill-progress-details__block--grid">
                  <Label>{label.labelName}</Label>
                  <Input
                    type="date"
                    value={serverDate ? toInputStandardDate(serverDate) : ""}
                    name={`${index}`}
                    disabled
                  />
                </div>
              );
            }
            if (label.dataType === DataTypes.DATE) {
              return (
                <div key={index} className="fill-progress-details__block--grid">
                  <Label>{label.labelName}</Label>
                  <Input
                    disabled={label.disabled}
                    type="date"
                    value={formData[index]}
                    name={`${index}`}
                    onKeyDown={handleFormEvent}
                    onChange={handleValueChange}
                  />
                </div>
              );
            }

            if (
              label.dataType === DataTypes.NUMBER ||
              label.dataType === DataTypes.FLOAT
            ) {
              return (
                <div key={index} className="fill-progress-details__block--grid">
                  <Label>{label.labelName}</Label>
                  <InputNumber
                    disabled={label.disabled}
                    value={formData[index]}
                    onKeyDown={handleFormEvent}
                    name={`${index}`}
                    onChange={handleValueChange}
                  />
                </div>
              );
            }

            if (label.dataType === DataTypes.BOOLEAN) {
              return (
                <div key={index} className="fill-progress-details__block--grid">
                  <Label>{label.labelName}</Label>
                  <FormControlLabel
                    label=""
                    labelPlacement="start"
                    name={index.toString()}
                    disabled={label.disabled}
                    control={
                      <AntSwitch
                        checked={Boolean(formData[`${index}`]) as boolean}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            [index]: e.target.checked,
                          });
                        }}
                        onKeyDown={handleFormEvent}
                      />
                    }
                    className="items__add--form-labels"
                  />
                </div>
              );
            }

            return (
              <div key={index} className="fill-progress-details__block--grid">
                <Label>{label.labelName}</Label>
                <Input
                  disabled={label.disabled}
                  value={formData[index]}
                  onKeyDown={handleFormEvent}
                  name={`${index}`}
                  onChange={handleValueChange}
                />
              </div>
            );
          })}
        </div>
        <Button mode="save" loading={loading} onClick={handleSubmit} />
        <Button mode="clear" loading={loading} />
        <Button
          mode="cancel"
          loading={loading}
          onClick={(e: any) => {
            e.preventDefault();
            setDrawer(false);
          }}
        />
      </form>
      <MessageModal
        handleClose={() => {
          setMessage({
            flag: false,
            message: "",
            operation: Operation.NONE,
          });
        }}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={updateLoading} />
    </>
  );
};

export default FillProgressDetails;
